import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Grid,
  Switch,
  Chip,
  Box,
  Stack,
  IconButton
} from '@mui/material';
import { 
  Psychology as BrainIcon,
  Person as UserIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowLeftIcon,
  ArrowForward as ArrowRightIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ProceduralMemoryBox from '../Boxes/Memories/ProceduralMemoryBox';
import SemanticMemoryBox from '../Boxes/Memories/SemanticMemoryBox';
import WorkingMemoryBox from '../Boxes/Memories/WorkingMemoryBox';
import EpisodicMemoryBox from '../Boxes/Memories/EpisodicMemoryBox';
import IDBox from '../Boxes/IDBox';
import AgentNode from '../../models/Nodes/AgentNode';
import SemanticMemory from '../../models/Memories/SemanticMemory';
import ProceduralMemory from '../../models/Memories/ProceduralMemory';
import { useEnvironment } from '../../context/EnvironmentContext';
import WorkflowService from '../../services/WorkflowService';
import { AVAILABLE_MODELS, DEFAULT_MODEL, DEFAULT_TEMPERATURE } from '../../config/models';
import PineconeConnection from '../../models/Connections/PineconeConnection';
import AWSConnection from '../../models/Connections/AWSConnection';
import MongoDBConnection from '../../models/Connections/MongoDBConnection';

const getCostColor = (cost: string) => {
  switch (cost) {
    case 'Very High': return 'error';
    case 'High': return 'warning';
    case 'Medium': return 'primary';
    case 'Low': return 'success';
    case 'Very Low': return 'info';
    default: return 'default';
  }
};

interface AgentCreateWizardModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (agent: AgentNode) => void;
}

// Utility to ensure all connections are class instances
function ensureConnectionInstance(conn: any) {
  if (conn instanceof PineconeConnection || conn instanceof AWSConnection || conn instanceof MongoDBConnection) {
    return conn;
  }
  if (conn.type === 'pinecone') return new PineconeConnection(conn);
  if (conn.type === 'aws') return new AWSConnection(conn);
  if (conn.type === 'mongodb_json') return new MongoDBConnection(conn);
  throw new Error('Unknown connection type');
}

const AgentCreateWizardModal: React.FC<AgentCreateWizardModalProps> = ({ 
  open, 
  onClose, 
  onUpdate
}) => {
  const { environment } = useEnvironment();
  const { enqueueSnackbar } = useSnackbar();
  const [currentStep, setCurrentStep] = useState(0);
  const [mode, setMode] = useState<'new' | 'template'>('new');
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentAgent, setCurrentAgent] = useState<AgentNode | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [requiredParameters, setRequiredParameters] = useState<string[]>([]);
  const [newParameter, setNewParameter] = useState('');
  const [activeMemoryTab, setActiveMemoryTab] = useState(0);
  const [workflowInputs, setWorkflowInputs] = useState<Record<string, any>>({});
  const [model, setModel] = useState<string>(DEFAULT_MODEL);
  const [modelTemperature, setModelTemperature] = useState<number>(DEFAULT_TEMPERATURE);
  const [agentType, setAgentType] = useState<'agent_template' | 'input_agent'>('agent_template');

  const wizardSteps = [
    {
      title: 'Agent Type',
      description: 'Choose creation method'
    },
    {
      title: 'Basic Info',
      description: 'Configure agent details'
    },
    {
      title: 'Model Config',
      description: 'Select AI model and parameters'
    },
    {
      title: 'Memories',
      description: 'Configure memory systems'
    },
    {
      title: 'Review',
      description: 'Review and create agent'
    }
  ];

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setCurrentAgent(null);
      setSelectedTemplate('');
      setRequiredParameters([]);
      setNewParameter('');
      setMode('new');
      setActiveMemoryTab(0);
      setWorkflowInputs({});
      setModel(DEFAULT_MODEL);
      setModelTemperature(DEFAULT_TEMPERATURE);
      setAgentType('agent_template');
    }
  }, [open]);

  // Initialize agent when modal opens in new mode
  useEffect(() => {
    if (open && mode === 'new' && !currentAgent && currentStep >= 1) {
      // Start with empty ID that user will fill in
      
      const newAgent = new AgentNode(
        '', // Start with empty ID
        new SemanticMemory(0.1, []),
        new ProceduralMemory(model, modelTemperature),
        agentType
      );
      setCurrentAgent(newAgent);
    }
  }, [open, mode, currentAgent, currentStep, model, modelTemperature, agentType]);

  // Fetch templates when in template mode
  useEffect(() => {
    if (open && mode === 'template') {
      fetchTemplates();
    }
  }, [open, mode, environment]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const workflowService = new WorkflowService(environment);
      const response = await workflowService.listAgents();
      const templatesData = Array.isArray(response.data?.agents) ? response.data.agents : [];
      setTemplates(templatesData);
    } catch (error) {
      enqueueSnackbar('Failed to fetch agent templates', { variant: 'error' });
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateDetails = async (templateId: string) => {
    try {
      setLoading(true);
      const workflowService = new WorkflowService(environment);
      const response = await workflowService.getAgentDetails(templateId);
      const templateData = response.data;

      const agent = new AgentNode(
        '', // Start with empty ID for template mode too
        SemanticMemory.fromJson(templateData.semantic_memory_args),
        ProceduralMemory.fromJson(templateData.procedural_memory_args),
        templateData.type || 'agent_template',
        null,
        null,
        [],
        templateId,
        [],
        [],
        undefined,
        templateData.uuid // Preserve UUID from template if available
      );
      agent.isEnabled = true;
      setCurrentAgent(agent);
      
      setRequiredParameters(templateData.required_parameters || []);
      setModel(templateData.procedural_memory_args?.model || DEFAULT_MODEL);
      setModelTemperature(templateData.procedural_memory_args?.model_temp || DEFAULT_TEMPERATURE);
      setAgentType(templateData.type || 'agent_template');
    } catch (error) {
      enqueueSnackbar('Failed to load template details', { variant: 'error' });
      console.error('Error loading template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMode(e.target.value as 'new' | 'template');
    setSelectedTemplate('');
    setCurrentAgent(null);
  };

  const handleAgentTypeChange = (type: 'agent_template' | 'input_agent') => {
    setAgentType(type);
    if (currentAgent) {
      const updatedAgent = new AgentNode(
        currentAgent.id,
        currentAgent.semanticMemory,
        currentAgent.proceduralMemory,
        type,
        null,
        null,
        [],
        currentAgent.parentAgentId,
        requiredParameters,
        [],
        undefined,
        currentAgent.uuid
      );
      updatedAgent.isEnabled = currentAgent.isEnabled;
      setCurrentAgent(updatedAgent);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    loadTemplateDetails(templateId);
  };

  const handleProceduralMemoryUpdate = (memory: ProceduralMemory) => {
    if (currentAgent) {
      const updatedAgent = new AgentNode(
        currentAgent.id,
        currentAgent.semanticMemory,
        memory,
        currentAgent.type,
        currentAgent.childData,
        currentAgent.loopElementsKey,
        currentAgent.dependencies,
        currentAgent.parentAgentId,
        requiredParameters,
        currentAgent.agentDependencies,
        currentAgent.agent_template_id,
        currentAgent.uuid
      );
      updatedAgent.isEnabled = currentAgent.isEnabled;
      setCurrentAgent(updatedAgent);
    }
  };

  const handleSemanticMemoryUpdate = (memory: SemanticMemory) => {
    if (currentAgent) {
      const updatedAgent = new AgentNode(
        currentAgent.id,
        memory,
        currentAgent.proceduralMemory,
        currentAgent.type,
        currentAgent.childData,
        currentAgent.loopElementsKey,
        currentAgent.dependencies,
        currentAgent.parentAgentId,
        requiredParameters,
        currentAgent.agentDependencies,
        currentAgent.agent_template_id,
        currentAgent.uuid
      );
      updatedAgent.isEnabled = currentAgent.isEnabled;
      setCurrentAgent(updatedAgent);
    }
  };

  const handleAddParameter = () => {
    if (newParameter.trim() && !requiredParameters.includes(newParameter.trim())) {
      setRequiredParameters(prev => [...prev, newParameter.trim()]);
      setNewParameter('');
    }
  };

  const handleRemoveParameter = (parameter: string) => {
    setRequiredParameters(prev => prev.filter(p => p !== parameter));
  };

  const handleNext = () => {
    if (currentStep < wizardSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    if (!currentAgent) {
      enqueueSnackbar('No agent to create', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      const workflowService = new WorkflowService(environment);
      
      // Create the agent
      const response = await workflowService.createAgent(currentAgent);
      
      if (response.success) {
        enqueueSnackbar('Agent created successfully', { variant: 'success' });
        onUpdate(currentAgent);
        onClose();
      } else {
        enqueueSnackbar('Failed to create agent', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Error creating agent', { variant: 'error' });
      console.error('Error creating agent:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderMemoryContent = () => {
    if (!currentAgent) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Memory Configuration</Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant={activeMemoryTab === 0 ? 'contained' : 'outlined'}
                onClick={() => setActiveMemoryTab(0)}
                startIcon={<BrainIcon />}
              >
                Procedural
              </Button>
              <Button
                variant={activeMemoryTab === 1 ? 'contained' : 'outlined'}
                onClick={() => setActiveMemoryTab(1)}
                startIcon={<BrainIcon />}
              >
                Semantic
              </Button>
              <Button
                variant={activeMemoryTab === 2 ? 'contained' : 'outlined'}
                onClick={() => setActiveMemoryTab(2)}
                startIcon={<BrainIcon />}
              >
                Working
              </Button>
              <Button
                variant={activeMemoryTab === 3 ? 'contained' : 'outlined'}
                onClick={() => setActiveMemoryTab(3)}
                startIcon={<BrainIcon />}
              >
                Episodic
              </Button>
            </Stack>
          </Box>
          
          {activeMemoryTab === 0 && (
            <ProceduralMemoryBox
              memory={currentAgent.proceduralMemory || new ProceduralMemory(DEFAULT_MODEL, DEFAULT_TEMPERATURE, [], {
                learn: { enabled: false, steps: [] },
                retrieve: { enabled: false, steps: [] },
                reason: { enabled: false, steps: [] },
                design: { enabled: false, steps: [] },
                reflect: { enabled: false, steps: [] }
              }, '')}
              onMemoryChange={handleProceduralMemoryUpdate}
              workflowInputs={workflowInputs}
              setWorkflowInputs={setWorkflowInputs}
              nodeType="agent"
            />
          )}
          
          {activeMemoryTab === 1 && (
            <SemanticMemoryBox
              memory={(() => {
                const mem = currentAgent.semanticMemory || new SemanticMemory(0.1, []);
                const safeConnections = mem.connections.map(ensureConnectionInstance);
                return new SemanticMemory(mem.similarityThreshold, safeConnections);
              })()}
              onMemoryChange={handleSemanticMemoryUpdate}
            />
          )}
          
          {activeMemoryTab === 2 && (
            <WorkingMemoryBox />
          )}
          
          {activeMemoryTab === 3 && (
            <EpisodicMemoryBox />
          )}
        </CardContent>
      </Card>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Agent Type
        return (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Creation Method</Typography>
                <RadioGroup value={mode} onChange={handleModeChange}>
                  <FormControlLabel
                    value="new"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="medium">Create New Agent</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Start from scratch with a blank agent configuration
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="template"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="medium">Use Template</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Start with an existing agent template
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </CardContent>
            </Card>

            {mode === 'template' && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Select Template</Typography>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <FormControl fullWidth>
                      <InputLabel>Template</InputLabel>
                      <Select
                        value={selectedTemplate}
                        onChange={(e) => handleTemplateChange(e.target.value)}
                        label="Template"
                      >
                        {templates.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.name || template.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </CardContent>
              </Card>
            )}
          </Stack>
        );

      case 1: // Basic Info
        return (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Agent Information</Typography>
                                 <Stack spacing={2}>
                   <Box>
                     <Typography variant="subtitle1" gutterBottom>Agent ID</Typography>
                     <TextField
                       fullWidth
                       value={currentAgent?.id || ''}
                       onChange={(e) => {
                         if (currentAgent) {
                           const updatedAgent = new AgentNode(
                             e.target.value,
                             currentAgent.semanticMemory,
                             currentAgent.proceduralMemory,
                             currentAgent.type,
                             currentAgent.childData,
                             currentAgent.loopElementsKey,
                             currentAgent.dependencies,
                             currentAgent.parentAgentId,
                             requiredParameters,
                             currentAgent.agentDependencies,
                             currentAgent.agent_template_id,
                             currentAgent.uuid
                           );
                           updatedAgent.isEnabled = currentAgent.isEnabled;
                           setCurrentAgent(updatedAgent);
                         }
                       }}
                       placeholder="Enter agent ID"
                     />
                   </Box>
                   <Box>
                     <Typography variant="subtitle1" gutterBottom>Agent Type</Typography>
                     <RadioGroup value={agentType} onChange={(e) => handleAgentTypeChange(e.target.value as 'agent_template' | 'input_agent')}>
                       <FormControlLabel
                         value="agent_template"
                         control={<Radio />}
                         label="Agent Template"
                       />
                       <FormControlLabel
                         value="input_agent"
                         control={<Radio />}
                         label="Input Agent"
                       />
                     </RadioGroup>
                   </Box>
                 </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Required Parameters</Typography>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      value={newParameter}
                      onChange={(e) => setNewParameter(e.target.value)}
                      placeholder="Parameter name"
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddParameter}
                      disabled={!newParameter.trim() || requiredParameters.includes(newParameter.trim())}
                    >
                      Add
                    </Button>
                  </Stack>
                  
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {requiredParameters.map((param) => (
                      <Chip
                        key={param}
                        label={param}
                        onDelete={() => handleRemoveParameter(param)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        );

      case 2: // Model Config
        return (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Model Configuration</Typography>
                                 <Stack direction="row" spacing={2}>
                   <Box sx={{ flex: 1 }}>
                     <FormControl fullWidth>
                       <InputLabel>Model</InputLabel>
                       <Select
                         value={model}
                         onChange={(e) => setModel(e.target.value)}
                         label="Model"
                       >
                         {AVAILABLE_MODELS.map((modelOption) => (
                           <MenuItem key={modelOption.name} value={modelOption.name}>
                             <Stack direction="row" spacing={1} alignItems="center">
                               <Typography variant="body2">{modelOption.name}</Typography>
                               <Chip 
                                 label={modelOption.cost} 
                                 size="small"
                                 color={getCostColor(modelOption.cost)}
                               />
                             </Stack>
                           </MenuItem>
                         ))}
                       </Select>
                     </FormControl>
                   </Box>
                   <Box sx={{ flex: 1 }}>
                     <TextField
                       fullWidth
                       label="Temperature"
                       type="number"
                       value={modelTemperature}
                       onChange={(e) => setModelTemperature(parseFloat(e.target.value) || 0)}
                       inputProps={{ min: 0, max: 1, step: 0.01 }}
                       helperText="Controls randomness (0.0 = deterministic, 1.0 = very random)"
                     />
                   </Box>
                 </Stack>
              </CardContent>
            </Card>
          </Stack>
        );

      case 3: // Memories
        return renderMemoryContent();

      case 4: // Review
        return (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Configuration Summary</Typography>
                                 <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                   <Box sx={{ flex: 1, minWidth: 200 }}>
                     <Typography variant="body2" fontWeight="bold">Agent ID:</Typography>
                     <Typography variant="body2">{currentAgent?.id || 'Not set'}</Typography>
                   </Box>
                   <Box sx={{ flex: 1, minWidth: 200 }}>
                     <Typography variant="body2" fontWeight="bold">Agent Type:</Typography>
                     <Chip label={agentType} color="primary" />
                   </Box>
                   <Box sx={{ flex: 1, minWidth: 200 }}>
                     <Typography variant="body2" fontWeight="bold">Model:</Typography>
                     <Typography variant="body2">{model}</Typography>
                   </Box>
                   <Box sx={{ flex: 1, minWidth: 200 }}>
                     <Typography variant="body2" fontWeight="bold">Temperature:</Typography>
                     <Typography variant="body2">{modelTemperature}</Typography>
                   </Box>
                   <Box sx={{ flex: 1, minWidth: 200 }}>
                     <Typography variant="body2" fontWeight="bold">Parameters:</Typography>
                     <Typography variant="body2">{requiredParameters.length} required</Typography>
                   </Box>
                 </Stack>
              </CardContent>
            </Card>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box sx={{ 
          width: 32, 
          height: 32, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'white'
        }}>
          <UserIcon />
        </Box>
        <Typography variant="h5" fontWeight="bold">
          Create Agent Wizard
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ py: 3 }}>
        <Stepper 
          activeStep={currentStep} 
          alternativeLabel
          sx={{ mb: 4 }}
        >
          {wizardSteps.map((step, index) => (
            <Step 
              key={step.title} 
              completed={index < currentStep}
              onClick={() => setCurrentStep(index)}
              sx={{ cursor: 'pointer' }}
            >
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ minHeight: 400 }}>
          {renderStepContent()}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={handlePrev}
          disabled={currentStep === 0}
          startIcon={<ArrowLeftIcon />}
          variant="outlined"
        >
          Previous
        </Button>
        
        <Box sx={{ flexGrow: 1 }} />
        
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        
        {currentStep < wizardSteps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowRightIcon />}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleFinish}
            startIcon={<CheckCircleIcon />}
            color="success"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Create Agent'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AgentCreateWizardModal;